import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Storage } from '@ionic/storage-angular';
import { Observable } from 'rxjs';
import { ApiClass, classCardImageUrl, classPrimaryImageUrl, classRecordId } from 'src/app/models/class';
import { Session } from 'src/app/models/session';
import { StorageService } from 'src/app/services/_core/storage/storage.service';

export interface CartEntry {
  courseId: string;
  courseName: string;
  courseImage: string;
  sessionId: string;
  /** Session duration label (e.g. from Duration__c). */
  sessionTitle: string;
  startsAt: string;
  quantity: number;
  unitCost: number;
  instructor?: string;
  sessionType?: string;
  location?: string;
  courseAudience?: string;
}

const CART_STORAGE_KEY = 'shoppingCartEntries';
const WISHLIST_STORAGE_KEY = 'shoppingCartWishlistEntries';

function asCartEntryArray(value: unknown): CartEntry[] {
  if (!Array.isArray(value)) {
    return [];
  }
  return value.filter(
    (e): e is CartEntry =>
      !!e &&
      typeof e === 'object' &&
      typeof (e as CartEntry).sessionId === 'string' &&
      typeof (e as CartEntry).quantity === 'number'
  );
}

@Injectable({
  providedIn: 'root'
})
export class CartService {
  private readonly storageService = inject(StorageService);
  private readonly ionStorage = inject(Storage);

  private readonly _cartItems = signal<CartEntry[]>([]);
  private readonly _wishlistItems = signal<CartEntry[]>([]);

  /** In-memory cart; mirrored to Ionic Storage until lines are removed or cleared. */
  readonly cartItems = this._cartItems.asReadonly();
  readonly wishlistItems = this._wishlistItems.asReadonly();

  readonly cartItems$: Observable<CartEntry[]>;
  readonly wishlistItems$: Observable<CartEntry[]>;

  /** Single flight; started from APP_INITIALIZER so UI never races a late apply. */
  private hydratePromise: Promise<void> | null = null;

  constructor() {
    this.cartItems$ = toObservable(this._cartItems);
    this.wishlistItems$ = toObservable(this._wishlistItems);
  }

  getItems(): CartEntry[] {
    return this._cartItems();
  }

  getTotalCount(): number {
    return this.getItems().reduce((sum, item) => sum + item.quantity, 0);
  }

  getWishlist(): CartEntry[] {
    return this._wishlistItems();
  }

  removeFromCart(sessionId: string): void {
    this.replaceCart(this.getItems().filter((item) => item.sessionId !== sessionId));
  }

  /** Remove from cart and add to wishlist (deduped by session). */
  saveForLater(sessionId: string): void {
    const entry = this.getItems().find((item) => item.sessionId === sessionId);
    if (!entry) {
      return;
    }
    this.replaceCart(this.getItems().filter((item) => item.sessionId !== sessionId));
    const wish = [...this.getWishlist()];
    if (!wish.some((w) => w.sessionId === sessionId)) {
      wish.push(entry);
    }
    this.replaceWishlist(wish);
  }

  removeFromWishlist(sessionId: string): void {
    this.replaceWishlist(this.getWishlist().filter((w) => w.sessionId !== sessionId));
  }

  addFromAvailability(
    course: ApiClass | undefined,
    sessions: Session[],
    selectedItems: { sessionId: string; quantity: number }[]
  ): void {
    if (!selectedItems.length) {
      return;
    }
    const current = [...this.getItems()];
    for (const selected of selectedItems) {
      if (!selected.sessionId || selected.quantity <= 0) {
        continue;
      }
      const session = sessions.find((s) => s.id === selected.sessionId);
      const existing = current.find((item) => item.sessionId === selected.sessionId);
      if (existing) {
        existing.quantity += selected.quantity;
        existing.instructor = (session?.instructor ?? '').trim() || existing.instructor;
        existing.sessionType = (session?.sessionType ?? '').trim() || existing.sessionType;
        existing.location = (session?.location ?? '').trim() || existing.location;
        continue;
      }
      const legacyCourse = course as (ApiClass & {
        id?: string;
        name?: string;
        classImage?: string;
        courseImage?: string;
        courseAudience?: string;
      }) | undefined;
      current.push({
        courseId: course ? classRecordId(course) || (legacyCourse?.id ?? '').trim() : '',
        courseName: (course?.name ?? legacyCourse?.name ?? '').trim() || 'Class',
        courseImage: this.resolveCourseImageRaw(course, legacyCourse),
        sessionId: selected.sessionId,
        sessionTitle: (session?.duration ?? '').trim() || 'Session',
        startsAt: (session?.startTime ?? '').trim(),
        quantity: selected.quantity,
        unitCost: Number(session?.cost ?? 0) || 0,
        instructor: (session?.instructor ?? '').trim(),
        sessionType: (session?.sessionType ?? '').trim(),
        location: (session?.location ?? '').trim(),
        courseAudience: (legacyCourse?.courseAudience ?? '').trim()
      });
    }
    this.replaceCart(current);
  }

  private resolveCourseImageRaw(
    course: ApiClass | undefined,
    legacyCourse?: {
      classImage?: string;
      courseImage?: string;
    }
  ): string {
    if (!course && !legacyCourse) {
      return '';
    }
    if (course) {
      const fromSlots = classPrimaryImageUrl(course);
      if (fromSlots) {
        return fromSlots;
      }
      const fromGallery = classCardImageUrl(course);
      if (fromGallery) {
        return fromGallery;
      }
    }
    return (legacyCourse?.classImage ?? legacyCourse?.courseImage ?? '').trim();
  }



  private replaceCart(items: CartEntry[]): void {
    this._cartItems.set(items);
    void this.storageService.saveData(CART_STORAGE_KEY, items, false);
  }

  private replaceWishlist(items: CartEntry[]): void {
    this._wishlistItems.set(items);
    void this.storageService.saveData(WISHLIST_STORAGE_KEY, items, false);
  }

  /** Load persisted cart and wishlist; safe to call multiple times (single flight). */
  hydrateFromStorage(): Promise<void> {
    if (!this.hydratePromise) {
      this.hydratePromise = this.runHydrateFromStorage();
    }
    return this.hydratePromise;
  }

  private async runHydrateFromStorage(): Promise<void> {
    try {
      await this.ionStorage.create();
    } catch {
      // Storage may still be usable via StorageService
    }
    const [cartRaw, wishRaw] = await Promise.all([
      this.storageService.getData(CART_STORAGE_KEY, false),
      this.storageService.getData(WISHLIST_STORAGE_KEY, false)
    ]);
    this._cartItems.set(asCartEntryArray(cartRaw));
    this._wishlistItems.set(asCartEntryArray(wishRaw));
  }

  /** Remove from wishlist and add back to cart (merge quantity if the same session is already in cart). */
  returnToCart(sessionId: string): void {
    const id = String(sessionId ?? '').trim();
    const entry = this.getWishlist().find((item) => String(item.sessionId ?? '').trim() === id);
    if (!entry) {
      return;
    }
    this.replaceWishlist(this.getWishlist().filter((w) => String(w.sessionId ?? '').trim() !== id));
    const current = [...this.getItems()];
    const existing = current.find((item) => String(item.sessionId ?? '').trim() === id);
    if (existing) {
      existing.quantity += entry.quantity;
    } else {
      current.push(entry);
    }
    this.replaceCart(current);
  }
}
